import { Controller } from "@hotwired/stimulus";
import Swiper from "swiper";
import { Navigation } from "swiper/modules";

/**
 * Itinerary Slider Controller
 *
 * Handles the Swiper slider for the itinerary-slider block.
 * Features:
 * - Tab navigation that syncs with slider
 * - Click on tabs to navigate to specific slides
 * - Active tab styling updates on slide change
 */
export default class extends Controller {
  static targets = ["container", "prevButton", "nextButton", "tab"];

  connect() {
    this.initSwiper();
  }

  disconnect() {
    if (this.swiper && this.containerTarget?.swiper === this.swiper) {
      this.swiper.destroy(true, true);
      delete this.containerTarget.swiper;
      this.swiper = null;
    }
  }

  /**
   * Initialize Swiper instance
   */
  initSwiper() {
    if (!this.hasContainerTarget) return;

    // Prevent duplicate Swiper instances if the controller binds more than once
    if (this.containerTarget.swiper) {
      this.swiper = this.containerTarget.swiper;
      this.updateNavigationVisibility();
      this.updateActiveTabs();
      return;
    }

    this.swiper = new Swiper(this.containerTarget, {
      modules: [Navigation],
      slidesPerView: "auto",
      spaceBetween: 40,
      grabCursor: true,
      watchOverflow: true,
      navigation: {
        prevEl: this.hasPrevButtonTarget ? this.prevButtonTarget : null,
        nextEl: this.hasNextButtonTarget ? this.nextButtonTarget : null,
        disabledClass: "opacity-30 cursor-not-allowed",
      },
      breakpoints: {
        // Mobile: smaller gap
        0: {
          spaceBetween: 16,
        },
        // Tablet and up: larger gap
        768: {
          spaceBetween: 40,
        },
      },
      on: {
        slideChange: () => this.updateActiveTabs(),
      },
    });

    // Keep reference on the element to avoid re-initialization
    this.containerTarget.swiper = this.swiper;

    this.updateNavigationVisibility();
    this.updateActiveTabs();
  }

  /**
   * Navigate to previous slide
   */
  prev() {
    if (this.swiper) {
      this.swiper.slidePrev();
    }
  }

  /**
   * Navigate to next slide
   */
  next() {
    if (this.swiper) {
      this.swiper.slideNext();
    }
  }

  /**
   * Go to specific slide when clicking a tab
   */
  goToSlide(event) {
    if (!this.swiper) return;

    const tab = event.currentTarget;
    const index = parseInt(tab.dataset.index, 10);

    if (!isNaN(index)) {
      this.swiper.slideTo(index);
    }
  }

  /**
   * Update active state of all tabs based on current slide
   */
  updateActiveTabs() {
    if (!this.swiper || !this.hasTabTarget) return;

    const activeIndex = this.swiper.activeIndex;

    this.tabTargets.forEach((tab) => {
      const tabIndex = parseInt(tab.dataset.index, 10);
      const activeClass = tab.dataset.activeClass || "";
      const inactiveClass = tab.dataset.inactiveClass || "";

      // Remove all classes first
      const allClasses = [
        ...activeClass.split(" "),
        ...inactiveClass.split(" "),
      ].filter(Boolean);
      tab.classList.remove(...allClasses);

      // Add appropriate classes
      if (tabIndex === activeIndex) {
        if (activeClass) {
          tab.classList.add(...activeClass.split(" ").filter(Boolean));
        }
      } else {
        if (inactiveClass) {
          tab.classList.add(...inactiveClass.split(" ").filter(Boolean));
        }
      }
    });
  }

  /**
   * Update navigation button visibility based on slides
   */
  updateNavigationVisibility() {
    if (!this.swiper) return;

    const hasEnoughSlides = this.swiper.slides.length > 1;

    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.classList.toggle("hidden", !hasEnoughSlides);
    }
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.classList.toggle("hidden", !hasEnoughSlides);
    }
  }
}
